import { forwardRef, useContext, useEffect, useMemo } from 'react';
import { capitalize, useApi } from '@repo-level/core-front';
import { i18nInstance } from 'i18n';
import { useTranslation } from 'react-i18next';

import { termsAndConditions } from 'api/cms/termsAndConditions';
import { ModalDefaultProps } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { formatDateByLocale } from 'helpers/date';

import { ScStaticPage } from 'components/StaticModal/styled';
import { Locale } from 'languages';
import { useIsAustralia } from 'hooks/useIsAustralia';

export const TermsAndConditionsModal = forwardRef<
  HTMLDivElement,
  ModalDefaultProps
>(({ onClose }, ref) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('glossary');
  const { language } = i18nInstance;

  const { isMobileS } = useContext(CommonUtilsContext);

  const isAustralia = useIsAustralia();

  const [termsRequest, { pending, data }] = useApi(termsAndConditions);

  const modalSettings: ModalTemplateSettings = useMemo(() => {
    return isMobileS
      ? { type: 'mobile', mode: { type: 'bottom' } }
      : {
          type: 'desktop',
          size: 'large',
          mode: { type: 'default' },
        };
  }, [isMobileS]);

  useEffect(() => {
    termsRequest(isAustralia ? Locale.ENGLISH_AUSTRALIA : language);
  }, [termsRequest, language, isAustralia]);

  const formatDate = (date: string) => formatDateByLocale(date, locale);

  return (
    <>
      {pending ? (
        <Spinner size={64} absoluteCenter />
      ) : (
        data && (
          <ModalTemplate
            ref={ref}
            onClose={onClose}
            settings={modalSettings}
            title={data.titleH1}
          >
            <ScStaticPage>
              <div className="date">
                <p>
                  {capitalize(t('effectiveFrom'))}{' '}
                  <span>{formatDate(data.effectiveFrom)}</span>
                </p>
                <p>
                  {capitalize(t('lastUpdated'))}{' '}
                  <span>{formatDate(data.lastUpdated)}</span>
                </p>
                <p>
                  {capitalize(t('version'))} <span>{data.version}</span>
                </p>
              </div>
              <div dangerouslySetInnerHTML={{ __html: data.text }} />
            </ScStaticPage>
          </ModalTemplate>
        )
      )}
    </>
  );
});
